// src/pages/Overview.jsx
import {
  AccountBalance,
  Add,
  ArrowDownward,
  ArrowUpward,
  AttachMoney,
  Build,
  CardGiftcard,
  Close,
  DirectionsBus,
  DonutLarge,
  DonutLargeOutlined,
  Download,
  Flight,
  Gavel,
  Home,
  KeyboardArrowDown,
  LabelOutlined,
  LocalHospital,
  LocalMovies,
  Paid,
  People,
  Pets,
  Savings,
  School,
  ShoppingCart,
  Spa,
  StackedLineChart,
  Work,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemContent,
  Modal,
  ModalClose,
  Option,
  Select,
  Sheet,
  Stack,
  SvgIcon,
  Typography,
  Skeleton,
  CircularProgress,
} from "@mui/joy";
import ListDivider from "@mui/joy/ListDivider";
import { useColorScheme, useTheme } from "@mui/joy/styles";
import { useMediaQuery } from "@mui/material";
import axios from "axios";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useTransform,
  animate,
  color,
} from "framer-motion";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
  lazy,
  Suspense,
} from "react";
import { AiOutlineCalendar, AiOutlineTransaction } from "react-icons/ai";
import { Link as RouterLink } from "react-router";
import { ReactComponent as Arrow } from "../arrow-trend-down-svgrepo-com (5).svg";

// Lazy load heavy components
const LineChart = lazy(() => import("../components/LineChart"));
const PieChartSample = lazy(() => import("../components/PieChart"));
const IncomeExpense = lazy(
  () => import("../components/Transactions/IncomeExpense"),
);
const Budget = lazy(() => import("./Budget"));

/* ==================== CONSTANTS ==================== */

const FALLBACK_COLORS = [
  "#1976d2",
  "#388e3c",
  "#f57c00",
  "#d32f2f",
  "#7b1fa2",
  "#00796b",
  "#c2185b",
  "#455a64",
];

const ALERT_DURATION = 3500;
const CHART_ANIMATION_DURATION = 0.6;
const MODAL_ANIMATION_DURATION = 0.3;

/* ==================== UTILITY FUNCTIONS ==================== */

/**
 * Generate distinct HSL colors for category labels
 * @param {string[]} labels - Array of category labels
 * @returns {Object} Map of label to HSL color string
 */
const generateDistinctColorMap = (labels = []) => {
  if (!Array.isArray(labels)) return {};

  const unique = Array.from(new Set(labels.map((l) => String(l || ""))));
  unique.sort((a, b) => a.localeCompare(b));

  const n = Math.max(1, unique.length);
  const map = {};

  unique.forEach((label, i) => {
    const hue = Math.round((i * 360) / n);
    const sat = 66;
    const light = 48;
    map[label] = `hsl(${hue} ${sat}% ${light}%)`;
  });

  return map;
};

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Convert Date object to YYYY-MM-DD string in local timezone
 */
const localIsoDate = (d) => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/**
 * Parse YYYY-MM-DD string to Date object in local timezone
 */
const parseIsoToLocal = (iso) => {
  if (!iso || typeof iso !== "string") return new Date(NaN);
  const parts = iso.split("-");
  if (parts.length !== 3) return new Date(NaN);
  const [y, m, d] = parts.map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return new Date(NaN);
  }
  return new Date(y, m - 1, d);
};

/**
 * Format month range display
 */
const formatMonthRange = ({ year, monthIndex }) => {
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  const options = { year: "numeric", month: "long", day: "numeric" };
  return `${firstDay.toLocaleDateString(undefined, options)} - ${lastDay.toLocaleDateString(undefined, options)}`;
};

/**
 * Get last N months as options
 */
const getLastNMonthOptions = (n = 12) => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({ year: d.getFullYear(), monthIndex: d.getMonth() });
  }
  return opts;
};

/**
 * Safe parse month value from various formats
 */
function safeParseMonthVal(raw) {
  if (raw == null) return null;

  if (typeof raw === "object" && raw !== null) {
    if (Number.isFinite(raw.year) && Number.isFinite(raw.monthIndex)) {
      return { year: raw.year, monthIndex: raw.monthIndex };
    }
    if (Number.isFinite(raw.value)) {
      const s = String(raw.value);
      const [yStr, mStr] = s.split("-");
      const y = Number(yStr);
      const m = Number(mStr);
      if (Number.isFinite(y) && Number.isFinite(m)) {
        return { year: y, monthIndex: m };
      }
    }
  }

  const s = String(raw);
  const [yStr, mStr] = s.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
  return { year: y, monthIndex: m };
}

/**
 * Safely parse any date format to Date object
 */
const parseAnyDateFormat = (raw) => {
  if (!raw) return null;

  if (raw instanceof Date) {
    return isNaN(raw.getTime()) ? null : raw;
  }

  if (typeof raw === "number" && isFinite(raw)) {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    const match = trimmed.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);

    if (match) {
      const [, y, m, d] = match;
      const date = new Date(Number(y), Number(m) - 1, Number(d));
      if (!isNaN(date.getTime())) return date;
    }

    const fallback = new Date(trimmed);
    if (!isNaN(fallback.getTime())) return fallback;
  }

  return null;
};

/**
 * Format amount with optional compact notation
 */
const formatAmount = (n, compact = false) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "0";

  if (compact) {
    return new Intl.NumberFormat(undefined, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(num);
  }

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/* ==================== CUSTOM HOOKS ==================== */

/**
 * Custom hook for localStorage persistence
 */
const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error loading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value) => {
      try {
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        console.warn(`Error saving localStorage key "${key}":`, error);
      }
    },
    [key, storedValue],
  );

  return [storedValue, setValue];
};

/**
 * Custom hook for debouncing values
 */
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/* ==================== MEMOIZED COMPONENTS ==================== */

/**
 * Animated number component with smooth transitions
 */
const AnimatedNumber = memo(({ value, decimals = 2 }) => {
  const motionValue = useMotionValue(value);
  const rounded = useTransform(motionValue, (v) => v.toFixed(decimals));

  useEffect(() => {
    const controls = animate(motionValue, value, {
      duration: CHART_ANIMATION_DURATION,
      ease: "easeOut",
    });

    return controls.stop;
  }, [value, motionValue]);

  return <motion.span>{rounded}</motion.span>;
});

AnimatedNumber.displayName = "AnimatedNumber";

/**
 * Alert toast component
 */
const AlertToast = memo(({ alert, onClose }) => {
  if (!alert) return null;

  return (
    <motion.div
      key={alert.id}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{
        duration: MODAL_ANIMATION_DURATION,
        ease: [0.4, 0, 0.2, 1],
      }}
      style={{ width: "100%" }}
    >
      <Alert
        color={alert.severity === "danger" ? "danger" : alert.severity}
        variant="soft"
        sx={{ alignItems: "center", width: "100%" }}
        endDecorator={
          <IconButton
            size="sm"
            variant="plain"
            onClick={onClose}
            aria-label="close alert"
          >
            <Close fontSize="small" />
          </IconButton>
        }
      >
        {alert.message}
      </Alert>
    </motion.div>
  );
});

AlertToast.displayName = "AlertToast";

/**
 * Loading skeleton for charts
 */
const ChartSkeleton = memo(() => (
  <Box
    sx={{
      width: "100%",
      height: 320,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CircularProgress />
  </Box>
));

ChartSkeleton.displayName = "ChartSkeleton";

/**
 * Summary card component
 */
const SummaryCard = memo(
  ({ icon, label, amount, color, onAdd, isSmall, trend }) => (
    <Card
      component={motion.div}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
      variant="outlined"
      sx={{
        borderRadius: "2px",
        flex: 1,
        width: "100%",
        background: "inherit",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: color,
        },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <IconButton
          onClick={onAdd}
          size={isSmall ? "sm" : "md"}
          sx={{
            background: `${color}15`,
            "&:hover": {
              background: `${color}25`,
            },
          }}
        >
          <Add sx={{ color }} />
        </IconButton>

        <Divider orientation="vertical" />

        <Box sx={{ color }}>{icon}</Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            level="body-sm"
            fontFamily="monospace"
            sx={{ opacity: 0.8 }}
          >
            {label}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography level={isSmall ? "h4" : "h3"} fontFamily="sans-serif">
              ${amount}
            </Typography>
            {trend && (
              <Chip
                size="sm"
                variant="soft"
                color={trend > 0 ? "success" : "danger"}
                startDecorator={trend > 0 ? <TrendingUp /> : <TrendingDown />}
              >
                {Math.abs(trend)}%
              </Chip>
            )}
          </Stack>
        </Box>
      </Stack>
    </Card>
  ),
);

SummaryCard.displayName = "SummaryCard";

/**
 * Transaction list item component
 */
const TransactionItem = memo(
  ({
    transaction,
    index,
    categoryLabel,
    categoryIcon,
    formatAmount,
    mode,
    isSmall,
  }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.4,
        delay: 0.6 + index * 0.1,
        ease: "easeOut",
      }}
    >
      <ListItem
        sx={{
          transition: "all 0.2s ease",
          borderRadius: "2px",
          "&:hover": {
            backgroundColor:
              mode === "light" ? "rgba(0,0,0,0.02)" : "rgba(255,255,255,0.05)",
            transform: "translateX(4px)",
          },
        }}
      >
        <ListItemContent>
          <Typography
            fontFamily="monospace"
            level={isSmall ? "body-sm" : "body-md"}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              {categoryIcon}
            </Box>
            {transaction.description}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" pl={3}>
            <Typography
              level="body-xs"
              fontFamily="monospace"
              sx={{ color: "text.secondary" }}
            >
              {categoryLabel}
            </Typography>
            <Typography
              level="body-xs"
              fontFamily="monospace"
              sx={{ color: "text.tertiary" }}
            >
              •
            </Typography>
            <Typography
              level="body-xs"
              fontFamily="monospace"
              sx={{ color: "text.tertiary" }}
            >
              {new Date(transaction.date).toLocaleDateString()}
            </Typography>
          </Stack>
        </ListItemContent>

        <Chip
          variant={mode === "light" ? "outlined" : "soft"}
          color={transaction.amount > 0 ? "success" : "danger"}
          size={isSmall ? "sm" : "md"}
          startDecorator={
            transaction.amount > 0 ? (
              <ArrowUpward
                sx={{
                  fontSize: isSmall ? 16 : 18,
                  color: mode === "light" ? "black" : "white",
                }}
              />
            ) : (
              <ArrowDownward
                sx={{
                  fontSize: isSmall ? 16 : 18,
                  color: mode === "light" ? "black" : "white",
                }}
              />
            )
          }
        >
          <Typography
            level={isSmall ? "body-xs" : "body-sm"}
            fontWeight="lg"
            sx={{ color: mode === "light" ? "black" : "white" }}
          >
            ${formatAmount(Math.abs(Number(transaction.amount)))}
          </Typography>
        </Chip>
      </ListItem>
      <Divider />
    </motion.div>
  ),
);

TransactionItem.displayName = "TransactionItem";

/* ==================== MAIN COMPONENT ==================== */

const Overview = ({
  transactions = [],
  budgets = [],
  onAddTransaction,
  categories = [],
}) => {
  const { mode } = useColorScheme();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));

  /* ==================== STATE ==================== */

  // Modal state
  const [open, setOpen] = useState(false);
  const [formType, setFormType] = useState(null);

  // Chart type with localStorage persistence
  const [chartType, setChartType] = useLocalStorage("chartType", "line");

  // Transactions state
  const safeTransactions = useMemo(
    () => (Array.isArray(transactions) ? transactions : []),
    [transactions],
  );
  const [localTransactions, setLocalTransactions] = useState(safeTransactions);

  useEffect(() => {
    setLocalTransactions(Array.isArray(transactions) ? transactions : []);
  }, [transactions]);

  // Selected month with localStorage persistence
  const [selectedMonth, setSelectedMonth] = useLocalStorage(
    "selectedMonth",
    () => {
      const now = new Date();
      return {
        year: now.getFullYear(),
        monthIndex: now.getMonth(),
      };
    },
  );

  const monthOptions = useMemo(() => getLastNMonthOptions(12), []);

  const selectedMonthStr = useMemo(
    () => `${selectedMonth.year}-${pad2(selectedMonth.monthIndex + 1)}`,
    [selectedMonth],
  );

  const startOfMonthIso = useMemo(() => {
    const d = new Date(selectedMonth.year, selectedMonth.monthIndex, 1);
    return localIsoDate(d);
  }, [selectedMonth]);

  const endOfMonthIso = useMemo(() => {
    const d = new Date(selectedMonth.year, selectedMonth.monthIndex + 1, 0);
    return localIsoDate(d);
  }, [selectedMonth]);

  /* ==================== CATEGORY ICONS ==================== */

  const categoryIconMap = useMemo(
    () => ({
      none: <LabelOutlined sx={{ fontSize: 16 }} />,
      "food & groceries": <ShoppingCart sx={{ fontSize: 16 }} />,
      food: <ShoppingCart sx={{ fontSize: 16 }} />,
      groceries: <ShoppingCart sx={{ fontSize: 16 }} />,
      transportation: <DirectionsBus sx={{ fontSize: 16 }} />,
      "home & utilities": <Home sx={{ fontSize: 16 }} />,
      home: <Home sx={{ fontSize: 16 }} />,
      utilities: <Home sx={{ fontSize: 16 }} />,
      "personal & self-care": <Spa sx={{ fontSize: 16 }} />,
      personal: <Spa sx={{ fontSize: 16 }} />,
      "self-care": <Spa sx={{ fontSize: 16 }} />,
      health: <LocalHospital sx={{ fontSize: 16 }} />,
      "entertainment & leisure": <LocalMovies sx={{ fontSize: 16 }} />,
      entertainment: <LocalMovies sx={{ fontSize: 16 }} />,
      education: <School sx={{ fontSize: 16 }} />,
      "finance & banking": <AccountBalance sx={{ fontSize: 16 }} />,
      finance: <AccountBalance sx={{ fontSize: 16 }} />,
      "gifts & donations": <CardGiftcard sx={{ fontSize: 16 }} />,
      gifts: <CardGiftcard sx={{ fontSize: 16 }} />,
      travel: <Flight sx={{ fontSize: 16 }} />,
      "family & children": <People sx={{ fontSize: 16 }} />,
      family: <People sx={{ fontSize: 16 }} />,
      "work & business": <Work sx={{ fontSize: 16 }} />,
      work: <Work sx={{ fontSize: 16 }} />,
      pets: <Pets sx={{ fontSize: 16 }} />,
      "household maintenance": <Build sx={{ fontSize: 16 }} />,
      maintenance: <Build sx={{ fontSize: 16 }} />,
      "taxes & government": <Gavel sx={{ fontSize: 16 }} />,
      taxes: <Gavel sx={{ fontSize: 16 }} />,
      income: <AttachMoney sx={{ fontSize: 16 }} />,
    }),
    [],
  );

  const getIconForCategoryName = useCallback(
    (name) => {
      if (!name) return categoryIconMap.none;
      const key = String(name).trim().toLowerCase();

      if (categoryIconMap[key]) return categoryIconMap[key];

      const firstWord = key.split(" ")[0];
      if (categoryIconMap[firstWord]) return categoryIconMap[firstWord];

      for (const [k, icon] of Object.entries(categoryIconMap)) {
        if (k && key.includes(k)) return icon;
      }

      return categoryIconMap.none;
    },
    [categoryIconMap],
  );

  /* ==================== CATEGORY RESOLUTION ==================== */

  const idToNameMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(categories)) {
      categories.forEach((c) => {
        if (c && c.id !== undefined && c.name) {
          map.set(String(c.id), String(c.name));
        }
      });
    }
    return map;
  }, [categories]);

  const resolveCategoryLabel = useCallback(
    (tx) => {
      if (!tx) return "None";

      if (tx.category_name && String(tx.category_name).trim()) {
        return String(tx.category_name).trim();
      }

      const categoryId = tx.category_id ?? tx.category?.id ?? tx.category;
      if (categoryId != null && String(categoryId) !== "") {
        const idStr = String(categoryId);
        const fromMap = idToNameMap.get(idStr);
        if (fromMap) return fromMap;
        return `Category ${idStr}`;
      }

      return "None";
    },
    [idToNameMap],
  );

  /* ==================== FILTERED DATA ==================== */

  const filteredTransactions = useMemo(() => {
    if (!Array.isArray(localTransactions)) return [];

    return localTransactions.filter((t) => {
      const date = parseAnyDateFormat(t.date);
      if (!date) return false;

      return (
        date.getFullYear() === selectedMonth.year &&
        date.getMonth() === selectedMonth.monthIndex
      );
    });
  }, [localTransactions, selectedMonth]);

  const firstFive = useMemo(
    () => filteredTransactions.slice(0, 5),
    [filteredTransactions],
  );

  /* ==================== CALCULATIONS ==================== */

  const totalIncome = useMemo(
    () =>
      filteredTransactions
        .filter((t) => Number(t.amount) > 0)
        .reduce((sum, t) => sum + Number(t.amount), 0),
    [filteredTransactions],
  );

  const totalExpenses = useMemo(
    () =>
      filteredTransactions
        .filter((t) => Number(t.amount) < 0)
        .reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0),
    [filteredTransactions],
  );

  const netBalance = useMemo(
    () => totalIncome - totalExpenses,
    [totalIncome, totalExpenses],
  );

  // Chart data
  const chartRaw = useMemo(() => {
    const map = {};
    filteredTransactions.forEach((t) => {
      const amt = Number(t.amount);
      if (!isFinite(amt) || amt >= 0) return;

      const date = parseAnyDateFormat(t.date);
      if (!date) return;

      const key = localIsoDate(date);
      if (!key) return;

      map[key] = (map[key] || 0) + Math.abs(amt);
    });

    return Object.entries(map)
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredTransactions]);

  // Categories breakdown
  const categoriesBreakdown = useMemo(() => {
    const catMap = {};

    filteredTransactions.forEach((t) => {
      const amt = Number(t.amount);
      if (!isFinite(amt) || amt >= 0) return;

      const date = parseAnyDateFormat(t.date);
      if (!date) return;

      const dateKey = localIsoDate(date);
      if (!dateKey) return;

      const label = resolveCategoryLabel(t);

      if (!catMap[label]) {
        const categoryId = t.category_id ?? t.category?.id ?? t.category;
        catMap[label] = {
          id: categoryId != null ? categoryId : null,
          dates: {},
        };
      }

      catMap[label].dates[dateKey] =
        (catMap[label].dates[dateKey] || 0) + Math.abs(amt);
    });

    return catMap;
  }, [filteredTransactions, resolveCategoryLabel]);

  const categoriesForChart = useMemo(() => {
    const out = {};
    Object.entries(categoriesBreakdown).forEach(([label, obj]) => {
      out[label] = obj?.dates || {};
    });
    return out;
  }, [categoriesBreakdown]);

  const categoryTotals = useMemo(() => {
    return Object.entries(categoriesBreakdown)
      .map(([label, obj]) => {
        const dates = obj?.dates || {};
        const total = Object.values(dates).reduce(
          (sum, val) => sum + Number(val || 0),
          0,
        );
        return { label, total };
      })
      .sort((a, b) => b.total - a.total);
  }, [categoriesBreakdown]);

  const allCategoryLabels = useMemo(
    () => Object.keys(categoriesForChart),
    [categoriesForChart],
  );

  const categoriesColors = useMemo(
    () => generateDistinctColorMap(allCategoryLabels),
    [allCategoryLabels],
  );

  /* ==================== BUDGET STATE ==================== */

  const safeBudgets = useMemo(
    () => (Array.isArray(budgets) ? budgets : []),
    [budgets],
  );

  const [fetchedBudgets, setFetchedBudgets] = useState([]);
  const [budgetsLoading, setBudgetsLoading] = useState(false);
  const [budgetsError, setBudgetsError] = useState(null);

  const [categoriesList, setCategoriesList] = useState([]);
  const [catsLoading, setCatsLoading] = useState(false);

  const [showAddBudget, setShowAddBudget] = useState(false);
  const [newBudgetAmount, setNewBudgetAmount] = useState("");
  const [newBudgetCategory, setNewBudgetCategory] = useState(null);
  const [creatingBudget, setCreatingBudget] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  /* ==================== ALERT SYSTEM ==================== */

  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);

  const pushAlert = useCallback(
    (message, severity = "success", ttl = ALERT_DURATION) => {
      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
        alertTimerRef.current = null;
      }

      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setAlert({ id, message, severity });

      alertTimerRef.current = setTimeout(() => {
        setAlert(null);
        alertTimerRef.current = null;
      }, ttl);
    },
    [],
  );

  const removeAlert = useCallback(() => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }
    setAlert(null);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }
    };
  }, []);

  /* ==================== API CALLS ==================== */

  const loadBudgets = useCallback(async () => {
    setBudgetsLoading(true);
    setBudgetsError(null);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`/api/budgets?month=${selectedMonthStr}`, {
        headers,
      });
      setFetchedBudgets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load budgets:", err);
      setBudgetsError(
        err.response?.data?.message || err.message || "Failed to load budgets",
      );
      setFetchedBudgets([]);
    } finally {
      setBudgetsLoading(false);
    }
  }, [selectedMonthStr]);

  useEffect(() => {
    loadBudgets();
  }, [loadBudgets]);

  useEffect(() => {
    let cancelled = false;

    async function loadCats() {
      setCatsLoading(true);
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get("/api/categories", { headers });

        if (!cancelled) {
          setCategoriesList(
            Array.isArray(res.data)
              ? res.data.map((c) => ({
                  id: c?.id,
                  name: c?.name ?? c?.label ?? String(c?.id || ""),
                }))
              : [],
          );
        }
      } catch (err) {
        console.warn("Failed to load categories:", err.message);
        if (!cancelled) setCategoriesList([]);
      } finally {
        if (!cancelled) setCatsLoading(false);
      }
    }

    loadCats();
    return () => {
      cancelled = true;
    };
  }, []);

  const createBudget = useCallback(async () => {
    setCreateError(null);

    if (newBudgetCategory == null || newBudgetCategory === "") {
      setCreateError("Select a category");
      return;
    }

    const trimmedAmount = String(newBudgetAmount || "").trim();
    if (!trimmedAmount) {
      setCreateError("Enter an amount");
      return;
    }

    const amt = Number(trimmedAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setCreateError("Amount must be a positive number");
      return;
    }

    setCreatingBudget(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const cat = categoriesList.find(
        (c) => String(c.id) === String(newBudgetCategory),
      );
      const categoryName = cat?.name || null;

      const payload = {
        amount: amt,
        category_id: newBudgetCategory,
        month: selectedMonthStr,
        ...(categoryName ? { name: categoryName } : {}),
      };

      await axios.post("/api/budgets", payload, { headers });
      await loadBudgets();

      setShowAddBudget(false);
      setNewBudgetAmount("");
      setNewBudgetCategory(null);
      pushAlert("Budget created successfully", "success");
    } catch (err) {
      console.error("Create budget failed:", err);
      setCreateError(
        err.response?.data?.message || err.message || "Failed to create budget",
      );
    } finally {
      setCreatingBudget(false);
    }
  }, [
    newBudgetAmount,
    newBudgetCategory,
    categoriesList,
    selectedMonthStr,
    loadBudgets,
    pushAlert,
  ]);

  const deleteBudget = useCallback(
    async (budgetId) => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`/api/budgets/${budgetId}`, { headers });
        await loadBudgets();
        pushAlert("Budget deleted successfully", "success");
      } catch (err) {
        console.error("deleteBudget error:", err);
        pushAlert("Failed to delete budget", "danger");
        throw err;
      }
    },
    [loadBudgets, pushAlert],
  );

  const downloadReport = useCallback(async () => {
    setReportError(null);
    setReportLoading(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setReportError("You must be logged in to download reports");
        setReportLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get("/api/reports/monthly", {
        headers,
        params: { month: selectedMonthStr },
        responseType: "blob",
        timeout: 120000,
      });

      const contentType =
        res.headers["content-type"] || res.headers["Content-Type"] || "";
      if (!contentType.includes("pdf")) {
        const text = await res.data.text().catch(() => "");
        try {
          const json = JSON.parse(text);
          setReportError(json.message || "Invalid response from server");
        } catch {
          setReportError("Server returned invalid PDF data");
        }
        setReportLoading(false);
        return;
      }

      const blob = new Blob([res.data], { type: "application/pdf" });
      let filename = `spending-report-${selectedMonthStr}.pdf`;

      const cd =
        res.headers["content-disposition"] ||
        res.headers["Content-Disposition"];
      if (cd) {
        const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match) {
          try {
            filename = decodeURIComponent(utf8Match[1]);
          } catch (e) {
            console.warn("Failed to decode UTF-8 filename:", e);
          }
        } else {
          const quotedMatch = cd.match(/filename="([^"]+)"/i);
          if (quotedMatch) {
            filename = quotedMatch[1];
          } else {
            const unquotedMatch = cd.match(/filename=([^;]+)/i);
            if (unquotedMatch) {
              filename = unquotedMatch[1].trim();
            }
          }
        }
      }

      filename = filename.replace(/["']/g, "");

      if (window.navigator?.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);
      }

      pushAlert("Report downloaded successfully", "success");
    } catch (err) {
      console.error("Download report failed:", err);

      let errorMessage = "Failed to download report";

      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        errorMessage = "Request timed out. The report might be too large.";
      } else if (err.response?.status === 401) {
        errorMessage = "Session expired. Please log in again.";
      } else if (err.response?.status === 404) {
        errorMessage = "No transactions found for this month.";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setReportError(errorMessage);
    } finally {
      setReportLoading(false);
    }
  }, [selectedMonthStr, pushAlert]);

  /* ==================== HANDLERS ==================== */

  const handleAddTransaction = useCallback(
    (savedTx) => {
      if (!savedTx) return;

      setLocalTransactions((prev) => {
        if (savedTx.id != null) {
          const idx = prev.findIndex(
            (t) => String(t.id) === String(savedTx.id),
          );
          if (idx !== -1) {
            const copy = [...prev];
            copy[idx] = savedTx;
            return copy;
          }
        }
        return [savedTx, ...prev];
      });

      setOpen(false);
      pushAlert("Transaction added successfully", "success");

      if (typeof onAddTransaction === "function") {
        try {
          onAddTransaction(savedTx);
        } catch (err) {
          console.warn("Parent onAddTransaction threw:", err);
        }
      }
    },
    [onAddTransaction, pushAlert],
  );

  const handleChartChange = useCallback(
    (newType) => {
      setChartType(newType);
    },
    [setChartType],
  );

  const handleMonthChange = useCallback(
    (event, value) => {
      const parsed = safeParseMonthVal(value ?? event?.target?.value);
      if (parsed) {
        setSelectedMonth(parsed);
      }
    },
    [setSelectedMonth],
  );

  /* ==================== RENDER DATA ==================== */

  const pieItems = useMemo(
    () =>
      categoryTotals.map((c) => ({
        label: c.label,
        value: Number(c.total || 0),
      })),
    [categoryTotals],
  );

  const chartOptions = useMemo(
    () => [
      {
        value: "line",
        label: "Line Chart",
        icon: <StackedLineChart fontSize="small" />,
      },
      {
        value: "pie",
        label: "Donut Chart",
        icon: <DonutLarge fontSize="small" />,
      },
    ],
    [],
  );

  const budgetsToRender = useMemo(
    () =>
      fetchedBudgets.length > 0
        ? fetchedBudgets
        : safeBudgets.length > 0
          ? safeBudgets
          : [],
    [fetchedBudgets, safeBudgets],
  );

  const topCategoriesNode = useMemo(() => {
    return categoryTotals.map((c, i) => (
      <Stack
        key={`${c.label}-${i}`}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1.5}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0, flex: 1 }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              bgcolor:
                categoriesColors[c.label] ||
                FALLBACK_COLORS[i % FALLBACK_COLORS.length],
              borderRadius: 1,
              flexShrink: 0,
            }}
          />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{ display: "inline-flex", alignItems: "center", mr: 0.25 }}
            >
              {getIconForCategoryName(c.label)}
            </Box>
            <Typography
              fontFamily="monospace"
              level="body-sm"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={c.label}
            >
              {c.label}
            </Typography>
          </Stack>
        </Stack>

        <Typography
          fontFamily="monospace"
          level="body-sm"
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        >
          ${formatAmount(c.total, true)}
        </Typography>
      </Stack>
    ));
  }, [categoryTotals, categoriesColors, getIconForCategoryName]);

  /* ==================== RENDER ==================== */

  return (
    <>
      {/* Floating Alert */}
      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
          zIndex: 1400,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          minWidth: { xs: "calc(100% - 32px)", sm: 280 },
          maxWidth: { xs: "calc(100% - 32px)", sm: 400 },
          alignItems: "flex-start",
        }}
      >
        <AnimatePresence mode="wait">
          <AlertToast alert={alert} onClose={removeAlert} />
        </AnimatePresence>
      </Box>

      <Grid
        container
        spacing={{ xs: 1.5, sm: 2 }}
        sx={{ px: { xs: 0, sm: 0 } }}
      >
        {/* Header */}
        <Grid xs={12}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
              spacing={{ xs: 1.5, sm: 2 }}
            >
              <Chip
                variant={mode === "light" ? "plain" : "soft"}
                size={isSmall ? "sm" : "md"}
                sx={{
                  position: "relative",
                  zIndex: 0,
                  overflow: "hidden",
                }}
                startDecorator={
                  <AiOutlineCalendar
                    style={{
                      position: "static",
                      zIndex: 0,
                      pointerEvents: "none",
                    }}
                  />
                }
              >
                <Typography
                  fontFamily="monospace"
                  level={isSmall ? "body-sm" : "body-md"}
                >
                  {formatMonthRange(selectedMonth)}
                </Typography>
              </Chip>

              <Box sx={{ minWidth: { xs: "100%", sm: 240 } }}>
                <Select
                  size="sm"
                  value={`${selectedMonth.year}-${selectedMonth.monthIndex}`}
                  onChange={handleMonthChange}
                  startDecorator={<AiOutlineCalendar />}
                  variant="outlined"
                  sx={{ fontFamily: "monospace" }}
                  aria-label="Select month"
                >
                  {monthOptions.map((opt) => (
                    <Option
                      key={`${opt.year}-${opt.monthIndex}`}
                      value={`${opt.year}-${opt.monthIndex}`}
                    >
                      {new Date(opt.year, opt.monthIndex).toLocaleString(
                        undefined,
                        { month: "long", year: "numeric" },
                      )}
                    </Option>
                  ))}
                </Select>
              </Box>
            </Stack>

            <Divider sx={{ my: { xs: 2, sm: 3 } }} />

            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
              gap={1}
            >
              <Typography
                level={isSmall ? "h3" : "h2"}
                fontFamily="monospace"
                sx={{ textAlign: "center" }}
              >
                Financial Dashboard
              </Typography>
              <Typography
                fontFamily="monospace"
                level={isSmall ? "body-sm" : "body-md"}
                sx={{ textAlign: "center", opacity: 0.7 }}
              >
                Overview for the selected month
              </Typography>
            </Stack>

            <Divider sx={{ my: { xs: 2, sm: 3 } }} />
          </motion.div>
        </Grid>

        {/* Income/Expense Cards */}
        <Grid
          xs={12}
          component={motion.div}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1.5, sm: 2 }}
            alignItems="stretch"
            sx={{ width: {xs:"90%", sm: "100%"} }}
          >
            <SummaryCard
              icon={
                <SvgIcon
                  component={Arrow}
                  inheritViewBox
                  sx={{
                    fontSize: isSmall ? "medium" : "large",
                    transform: "rotate(-80deg)",
                    "& path": {
                      fill: mode === "light" ? "black" : "white",
                      stroke: mode === "light" ? "black" : "white",
                    },
                  }}
                />
              }
              label="Total Income"
              amount={formatAmount(totalIncome)}
              color="hsl(120, 60%, 50%)"
              onAdd={() => {
                setOpen(true);
                setFormType("income");
              }}
              isSmall={isSmall}
            />

            <Typography
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                fontSize: "1.5rem",
              }}
            >
              −
            </Typography>

            <SummaryCard
              icon={
                <SvgIcon
                  component={Arrow}
                  inheritViewBox
                  sx={{
                    fontSize: isSmall ? "medium" : "large",
                    transform: "rotate(0deg)",
                    "& path": {
                      fill: mode === "light" ? "black" : "white",
                      stroke: mode === "light" ? "black" : "white",
                    },
                  }}
                />
              }
              label="Total Expenses"
              amount={formatAmount(totalExpenses)}
              color="hsl(0, 70%, 55%)"
              onAdd={() => {
                setOpen(true);
                setFormType("expense");
              }}
              isSmall={isSmall}
            />
          </Stack>

          <Typography
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            sx={{ textAlign: "center", my: 1.5, fontSize: "1.5rem" }}
          >
            =
          </Typography>

          <Stack
            component={Card}
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="center"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            sx={{
              p: { xs: 2, sm: 3 },
              bgcolor: "inherit",
              borderRadius: "2px",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background:
                  netBalance >= 0 ? "hsl(120, 60%, 50%)" : "hsl(0, 70%, 55%)",
              },
            }}
          >
            <Savings
              fontSize={isSmall ? "medium" : "large"}
              sx={{
                color:
                  netBalance >= 0 ? "hsl(120, 60%, 50%)" : "hsl(0, 70%, 55%)",
              }}
            />
            <Box>
              <Typography
                fontFamily="monospace"
                level="body-sm"
                sx={{ opacity: 0.8 }}
              >
                Net Balance
              </Typography>
              <Typography
                level={isSmall ? "h4" : "h3"}
                fontFamily="sans-serif"
                sx={{
                  color:
                    netBalance >= 0 ? "hsl(120, 60%, 50%)" : "hsl(0, 70%, 55%)",
                }}
              >
                ${formatAmount(netBalance)}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: { xs: 2, sm: 3 } }} />
        </Grid>

        {/* Spending Chart */}
        <Grid xs={12}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Card
              sx={{
                overflow: "visible",
                background: "inherit",
                borderRadius: "2px",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                mb={2}
                sx={{ width: "100%" }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <DonutLarge fontSize={isSmall ? "medium" : "large"} />
                  <Typography
                    level={isSmall ? "title-lg" : "h4"}
                    fontFamily="monospace"
                  >
                    Spending Overview
                  </Typography>
                </Stack>

                <Select
                  value={chartType}
                  indicator={<KeyboardArrowDown />}
                  startDecorator={
                    chartType === "pie" ? (
                      <DonutLargeOutlined />
                    ) : (
                      <StackedLineChart />
                    )
                  }
                  size="sm"
                  slotProps={{
                    listbox: { sx: { "--ListItemDecorator-size": "44px" } },
                  }}
                  sx={{
                    "--ListItemDecorator-size": "44px",
                    minWidth: { xs: "100%", sm: 160 },
                  }}
                  onChange={(event, value) => {
                    if (value) handleChartChange(value);
                  }}
                >
                  {chartOptions.map((opt, idx) => (
                    <React.Fragment key={opt.value}>
                      {idx !== 0 && (
                        <ListDivider role="none" inset="startContent" />
                      )}
                      <Option value={opt.value} label={opt.label}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {opt.icon}
                          <Typography>{opt.label}</Typography>
                        </Stack>
                      </Option>
                    </React.Fragment>
                  ))}
                </Select>
              </Stack>

              <Box sx={{ p: { xs: 1, sm: 2 } }}>
                {chartRaw.length === 0 ? (
                  <Box
                    sx={{
                      py: 8,
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  >
                    <DonutLargeOutlined
                      sx={{ fontSize: 64, opacity: 0.3, mb: 2 }}
                    />
                    <Typography fontFamily="monospace" level="body-md">
                      No expense data to display
                    </Typography>
                    <Typography
                      fontFamily="monospace"
                      level="body-sm"
                      sx={{ mt: 1, opacity: 0.7 }}
                    >
                      Start adding transactions to see your spending patterns
                    </Typography>
                  </Box>
                ) : (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 3, sm: 2 }}
                    alignItems={{ xs: "center", sm: "flex-start" }}
                    sx={{ width: "100%" }}
                  >
                    <Suspense fallback={<ChartSkeleton />}>
                      <motion.div
                        key={chartType}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        style={{
                          flex: 1,
                          minWidth: isSmall ? "100%" : "400px",
                          minHeight: isSmall ? "280px" : "320px",
                          width: "100%",
                          maxWidth: "100%",
                          overflow: "visible",
                        }}
                      >
                        {chartType === "pie" ? (
                          <PieChartSample items={pieItems} donut={false} />
                        ) : (
                          <LineChart
                            data={chartRaw}
                            categories={categoriesForChart}
                            categoriesColors={categoriesColors}
                            startDate={startOfMonthIso}
                            endDate={endOfMonthIso}
                            maxHeight={320}
                          />
                        )}
                      </motion.div>
                    </Suspense>

                    <Stack
                      direction="column"
                      sx={{
                        gap: 1,
                        width: { xs: "100%", sm: 280 },
                        display: "flex",
                      }}
                    >
                      <Typography
                        fontFamily="monospace"
                        level="body-sm"
                        fontWeight="lg"
                      >
                        Top Categories
                      </Typography>
                      <Divider />
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          maxHeight: 320,
                          overflowY: "auto",
                          pr: 1,
                          "&::-webkit-scrollbar": {
                            width: "8px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "transparent",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: mode === "light" ? "#ccc" : "#555",
                            borderRadius: "2px",
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            background: mode === "light" ? "#aaa" : "#777",
                          },
                        }}
                      >
                        {categoryTotals.length === 0 ? (
                          <Typography
                            fontFamily="monospace"
                            color="text.secondary"
                            level="body-sm"
                          >
                            No categories to display
                          </Typography>
                        ) : (
                          topCategoriesNode
                        )}
                      </Box>
                    </Stack>
                  </Stack>
                )}
              </Box>
            </Card>
          </motion.div>
        </Grid>

        {/* Budget Progress */}
        <Suspense
          fallback={
            <Grid xs={12}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          }
        >
          <Budget
            transactions={transactions}
            showAddBudget={showAddBudget}
            setShowAddBudget={setShowAddBudget}
            budgetsLoading={budgetsLoading}
            budgetsError={budgetsError}
            budgetsToRender={budgetsToRender}
            categoryLabelFromId={(id) => {
              if (id == null) return "None";
              const mapped = idToNameMap.get(String(id));
              return mapped || `Category ${id}`;
            }}
            formatAmount={formatAmount}
            categoriesList={categoriesBreakdown}
            catsLoading={catsLoading}
            createError={createError}
            creatingBudget={creatingBudget}
            createBudget={createBudget}
            selectedMonthStr={selectedMonthStr}
            newBudgetAmount={newBudgetAmount}
            setNewBudgetAmount={setNewBudgetAmount}
            newBudgetCategory={newBudgetCategory}
            setNewBudgetCategory={setNewBudgetCategory}
            deleteBudget={deleteBudget}
            categoryIconMap={categoryIconMap}
          />
        </Suspense>

        {/* Monthly Report */}
        <Grid xs={12} md={6}>
          <Card sx={{ background: "inherit", borderRadius: "2px" }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Paid fontSize={isSmall ? "medium" : "large"} />
                <Typography
                  level={isSmall ? "title-lg" : "h4"}
                  fontFamily="monospace"
                >
                  Monthly Report
                </Typography>
              </Stack>

              <Link
                component={RouterLink}
                to="/budgets"
                fontFamily="monospace"
                level="body-sm"
              >
                View all
              </Link>
            </Stack>

            <Divider sx={{ my: 2 }}>
              <Chip variant={mode === "light" ? "outlined" : "soft"} size="sm">
                <Typography
                  fontFamily="monospace"
                  level="body-sm"
                  sx={{ color: mode === "light" ? "black" : "inherit" }}
                >
                  {new Date(
                    parseIsoToLocal(startOfMonthIso),
                  ).toLocaleDateString(undefined, {
                    month: "long",
                    year: "numeric",
                  })}
                </Typography>
              </Chip>
            </Divider>

            <Button
              onClick={downloadReport}
              disabled={reportLoading}
              loading={reportLoading}
              fullWidth={isSmall}
              startDecorator={!reportLoading && <Download />}
              sx={{ mt: 1 }}
            >
              {reportLoading ? "Preparing report..." : "Download PDF Report"}
            </Button>

            {reportError && (
              <Alert color="danger" variant="soft" sx={{ mt: 2 }}>
                <Typography fontFamily="monospace" level="body-sm">
                  {reportError}
                </Typography>
              </Alert>
            )}
          </Card>
        </Grid>

        {/* Recent Transactions */}
        <Grid xs={12}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Card sx={{ background: "inherit", borderRadius: "2px" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AiOutlineTransaction size={isSmall ? 20 : 24} />
                  <Typography
                    level={isSmall ? "title-md" : "h4"}
                    fontFamily="monospace"
                  >
                    Recent Transactions
                  </Typography>
                </Stack>

                <Link
                  component={RouterLink}
                  to="/transactions"
                  fontFamily="monospace"
                  level={{ xs: "body-xs", sm: "body-sm" }}
                >
                  View all
                </Link>
              </Stack>

              <List
                sx={{
                  "--ListItem-paddingY": { xs: "12px", sm: "16px" },
                  "--ListItem-paddingX": { xs: "8px", sm: "12px" },
                }}
              >
                {firstFive.length === 0 ? (
                  <Box
                    sx={{ py: 8, textAlign: "center", color: "text.secondary" }}
                  >
                    <AiOutlineTransaction
                      size={64}
                      style={{ opacity: 0.3, marginBottom: 16 }}
                    />
                    <Typography fontFamily="monospace" level="body-md">
                      No transactions this month
                    </Typography>
                    <Typography
                      fontFamily="monospace"
                      level="body-sm"
                      sx={{ mt: 1, opacity: 0.7 }}
                    >
                      Add your first transaction to get started
                    </Typography>
                  </Box>
                ) : (
                  firstFive.map((transaction, index) => (
                    <TransactionItem
                      key={transaction.id}
                      transaction={transaction}
                      index={index}
                      categoryLabel={resolveCategoryLabel(transaction)}
                      categoryIcon={getIconForCategoryName(
                        resolveCategoryLabel(transaction),
                      )}
                      formatAmount={formatAmount}
                      mode={mode}
                      isSmall={isSmall}
                    />
                  ))
                )}
              </List>
            </Card>
          </motion.div>
        </Grid>

        {/* Transaction Modal */}
        <AnimatePresence mode="wait">
          {open && (
            <Modal
              aria-labelledby="modal-title"
              aria-describedby="modal-desc"
              open={open}
              onClose={() => {
                setOpen(false);
                setFormType(null);
              }}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backdropFilter: "blur(8px)",
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{
                  duration: MODAL_ANIMATION_DURATION,
                  ease: [0.4, 0, 0.2, 1],
                }}
                style={{
                  maxWidth: isSmall ? "calc(100% - 32px)" : 500,
                  width: isSmall ? "calc(100% - 32px)" : "auto",
                }}
              >
                <Sheet
                  variant="outlined"
                  sx={{
                    borderRadius: "2px",
                    p: { xs: 2, sm: 3 },
                    boxShadow: "lg",
                    maxHeight: { xs: "90vh", sm: "auto" },
                    overflowY: "auto",
                  }}
                >
                  <ModalClose variant="plain" sx={{ m: 1 }} />
                  <Suspense fallback={<CircularProgress />}>
                    <IncomeExpense
                      categories={
                        Array.isArray(categories) ? categories : categoriesList
                      }
                      categoriesBreakdown={categoriesBreakdown}
                      onAddTransaction={handleAddTransaction}
                      formType={formType}
                      setOpen={setOpen}
                      selectedMonth={selectedMonth}
                      onAlert={pushAlert}
                    />
                  </Suspense>
                </Sheet>
              </motion.div>
            </Modal>
          )}
        </AnimatePresence>
      </Grid>
    </>
  );
};

export default memo(Overview);
